/**
 * Raw project shape coming from the API (snake_case).
 * All fields are optional so the transformer can handle both
 * partially-transformed and fully-raw objects.
 */
export interface RawProject {
  // snake_case API fields
  infrastructure_budget?: string | number | null;
  delivery_budget?: string | number | null;
  start_date?: string | null;
  end_date?: string | null;
  jira_integration_configured?: boolean | null;
  milestone_source?: string | null;
  last_sync?: string | null;
  created_at?: string | null;
  client_name?: string | null;
  client_id?: string | null;
  infrastructure_spent?: string | number | null;
  delivery_spent?: string | number | null;
  total_spent?: string | number | null;
  cost_tracking_enabled?: boolean | null;
  cost_tag_key?: string | null;
  cost_tag_value?: string | null;

  // camelCase fallbacks (already-transformed objects)
  infrastructureBudget?: string | number | null;
  deliveryBudget?: string | number | null;
  startDate?: string | null;
  endDate?: string | null;
  jiraConfigured?: boolean | null;
  milestoneSource?: string | null;
  lastSync?: string | null;
  createdAt?: string | null;
  clientName?: string | null;
  clientId?: string | null;
  infrastructureSpent?: string | number | null;
  deliverySpent?: string | number | null;
  totalSpent?: string | number | null;
  costTrackingEnabled?: boolean | null;
  costTagKey?: string | null;
  costTagValue?: string | null;

  // Passthrough fields
  name?: string;
  description?: string | null;
  currency?: string;
  status?: string;
  milestones?: unknown[];

  [key: string]: unknown;
}

/**
 * Normalised (camelCase) project shape used throughout the frontend.
 */
export interface ProjectData {
  infrastructureBudget: number;
  deliveryBudget: number;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
  jiraConfigured: boolean;
  milestoneSource: string | null | undefined;
  lastSync: string | null | undefined;
  createdAt: string | null | undefined;
  clientName: string | null | undefined;
  clientId: string | null | undefined;
  infrastructureSpent: number;
  deliverySpent: number;
  totalSpent: number;
  milestones: unknown[];
  costTrackingEnabled: boolean;
  costTagKey: string | null | undefined;
  costTagValue: string | null | undefined;

  // snake_case fields set to undefined to remove them
  infrastructure_budget: undefined;
  delivery_budget: undefined;
  start_date: undefined;
  end_date: undefined;
  jira_integration_configured: undefined;
  milestone_source: undefined;
  last_sync: undefined;
  created_at: undefined;
  client_name: undefined;
  client_id: undefined;
  infrastructure_spent: undefined;
  delivery_spent: undefined;
  total_spent: undefined;
  cost_tracking_enabled: undefined;
  cost_tag_key: undefined;
  cost_tag_value: undefined;

  [key: string]: unknown;
}

/**
 * Payload shape sent to the API (snake_case).
 */
export interface ProjectApiPayload {
  name?: string;
  description?: string | null;
  infrastructure_budget?: string | number | null;
  delivery_budget?: string | number | null;
  start_date?: string | null;
  end_date?: string | null;
  client_id?: string | null;
  currency: string;
  status?: string;
}

/**
 * Transform API response from snake_case to camelCase
 */
export const transformProjectData = (project: RawProject): RawProject & ProjectData => {
  const parseNumber = (value: string | number | null | undefined): number => {
    const num = Number.parseFloat(String(value));
    return Number.isNaN(num) ? 0 : num;
  };

  return {
    ...project,
    // Convert snake_case to camelCase
    infrastructureBudget: parseNumber(project.infrastructure_budget ?? project.infrastructureBudget),
    deliveryBudget: parseNumber(project.delivery_budget ?? project.deliveryBudget),
    startDate: project.start_date ?? project.startDate,
    endDate: project.end_date ?? project.endDate,
    jiraConfigured: project.jira_integration_configured ?? project.jiraConfigured ?? false,
    milestoneSource: project.milestone_source ?? project.milestoneSource,
    lastSync: project.last_sync ?? project.lastSync,
    createdAt: project.created_at ?? project.createdAt,
    clientName: project.client_name ?? project.clientName,
    clientId: project.client_id ?? project.clientId,
    infrastructureSpent: parseNumber(project.infrastructure_spent ?? project.infrastructureSpent),
    deliverySpent: parseNumber(project.delivery_spent ?? project.deliverySpent),
    totalSpent: parseNumber(project.total_spent ?? project.totalSpent),
    milestones: project.milestones ?? [],
    // Cost tracking fields
    costTrackingEnabled: project.cost_tracking_enabled ?? project.costTrackingEnabled ?? false,
    costTagKey: project.cost_tag_key ?? project.costTagKey,
    costTagValue: project.cost_tag_value ?? project.costTagValue,
    // Clean up - remove snake_case versions
    infrastructure_budget: undefined,
    delivery_budget: undefined,
    start_date: undefined,
    end_date: undefined,
    jira_integration_configured: undefined,
    milestone_source: undefined,
    last_sync: undefined,
    created_at: undefined,
    client_name: undefined,
    client_id: undefined,
    infrastructure_spent: undefined,
    delivery_spent: undefined,
    total_spent: undefined,
    cost_tracking_enabled: undefined,
    cost_tag_key: undefined,
    cost_tag_value: undefined
  };
};

/**
 * Transform project data from camelCase to snake_case for API requests
 */
export const transformProjectDataForAPI = (project: RawProject): ProjectApiPayload => {
  const transformed: ProjectApiPayload = {
    name: project.name,
    description: project.description,
    infrastructure_budget: project.infrastructureBudget,
    delivery_budget: project.deliveryBudget,
    start_date: project.startDate,
    end_date: project.endDate,
    client_id: project.clientId === '' ? undefined : project.clientId,
    currency: project.currency || 'USD',
    status: project.status
  };

  // Remove undefined values
  (Object.keys(transformed) as Array<keyof ProjectApiPayload>).forEach(key => {
    if (transformed[key] === undefined) {
      delete transformed[key];
    }
  });

  return transformed;
};
